import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, LayoutDashboard, LogOut, BarChart3, Zap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { displayNameForUser, type SessionUser } from "../lib/sessionUser";
import { fetchUserProfile, type UserProfile } from "../lib/userProfile";
import { getUserSummary, type UserSummary } from "../lib/subscriptionApi";
import { dashboardSummaryFromCache, readDashboardCache } from "../lib/dashboardCache";
import { LIVE_STATS_REFRESH_MS } from "../lib/liveRefresh";

type UserAccountMenuProps = {
  user: SessionUser;
  onSignOut: () => void;
};

function initialsForUser(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "U").toUpperCase();
}

const UserAccountMenu = ({ user, onSignOut }: UserAccountMenuProps) => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [summary, setSummary] = useState<UserSummary | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openMenu = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const loadProfile = useCallback(async () => {
    const next = await fetchUserProfile();
    if (next) setProfile(next);
  }, []);

  useEffect(() => { void loadProfile(); }, [loadProfile, user.id, user.email]);

  useEffect(() => {
    const refresh = () => void loadProfile();
    window.addEventListener("user-profile-changed", refresh);
    return () => window.removeEventListener("user-profile-changed", refresh);
  }, [loadProfile]);

  const accountEmail = profile?.email?.trim() || user.email?.trim() || "";

  const loadSummary = useCallback(async () => {
    if (!accountEmail) return;
    const cached = dashboardSummaryFromCache(accountEmail);
    if (cached) setSummary(cached);
    try {
      const fresh = await getUserSummary(accountEmail);
      setSummary(fresh);
    } catch {
      if (!cached) setSummary(null);
    }
  }, [accountEmail]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    const refresh = () => void loadSummary();
    window.addEventListener("user-mcp-changed", refresh);
    window.addEventListener("dashboard-data-updated", refresh);
    return () => {
      window.removeEventListener("user-mcp-changed", refresh);
      window.removeEventListener("dashboard-data-updated", refresh);
    };
  }, [loadSummary]);

  useEffect(() => {
    if (!open || !accountEmail) return;
    if (!readDashboardCache(accountEmail)) void loadSummary();
  }, [open, accountEmail, loadSummary]);

  // Keep avatar menu stats in sync while signed in (e.g. after MCP tool calls in Cursor).
  useEffect(() => {
    if (!accountEmail) return;
    const tick = () => {
      if (document.visibilityState === "visible") void loadSummary();
    };
    const intervalId = window.setInterval(tick, LIVE_STATS_REFRESH_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [accountEmail, loadSummary]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    return () => { if (closeTimer.current) clearTimeout(closeTimer.current); };
  }, []);

  const displayName = profile?.name?.trim() || displayNameForUser(user);
  const email = profile?.email?.trim() || user.email?.trim() || "";
  const imageUrl = user.image?.trim() || profile?.image?.trim() || "";
  const isPro = summary?.plan === "pro";
  const used = summary?.conversions_used_this_month ?? 0;
  const limit = summary?.monthly_limit ?? 3;
  const usagePct = Math.min(100, (used / limit) * 100);
  const atLimit = !isPro && used >= limit;
  const conversionsLeft = summary?.conversions_left ?? null;
  const showNudge = !isPro && conversionsLeft !== null && conversionsLeft > 0 && conversionsLeft <= 2;
  const statsPending = open && !summary;

  return (
    <div ref={wrapperRef} className="relative" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
      {/* Trigger */}
      <button
        type="button"
        className="rounded-full transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account menu for ${displayName}`}
      >
        <Avatar className="h-9 w-9 border-2 border-border">
          {imageUrl ? <AvatarImage src={imageUrl} alt="" /> : null}
          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
            {initialsForUser(displayName)}
          </AvatarFallback>
        </Avatar>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.96 }}
            transition={{ duration: 0.14, ease: "easeOut" }}
            role="menu"
            onMouseEnter={openMenu}
            onMouseLeave={scheduleClose}
            style={{
              position: "absolute",
              top: "calc(100% + 12px)",
              right: 0,
              width: 270,
              zIndex: 99999,
              borderRadius: 16,
              overflow: "hidden",
              background: "#ffffff",
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
            }}
          >
            {/* ── Header ── */}
            <div style={{
              padding: "16px 16px 14px",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              background: "#ffffff",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar className="h-10 w-10 border-2 border-border shrink-0">
                  {imageUrl ? <AvatarImage src={imageUrl} alt="" /> : null}
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm font-bold">
                    {initialsForUser(displayName)}
                  </AvatarFallback>
                </Avatar>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    className="text-foreground">
                    {displayName}
                  </p>
                  {email && (
                    <p style={{ fontSize: 12, margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      className="text-muted-foreground">
                      {email}
                    </p>
                  )}
                </div>

                {/* Single plan badge */}
                {isPro ? (
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "4px 10px", borderRadius: 99,
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0,
                    letterSpacing: "0.02em",
                  }}>
                    <Crown size={9} fill="white" strokeWidth={0} />
                    Pro
                  </span>
                ) : (
                  <span style={{
                    display: "inline-flex", alignItems: "center",
                    padding: "4px 10px", borderRadius: 99,
                    fontSize: 11, fontWeight: 600, flexShrink: 0,
                    letterSpacing: "0.02em",
                  }} className="bg-muted text-muted-foreground border border-border">
                    Free
                  </span>
                )}
              </div>
            </div>

            {/* ── Stats grid + conversions (all signed-in users) ── */}
            <div style={{ padding: "12px 14px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {[
                  { label: "Active MCPs", value: statsPending ? "—" : (summary?.active_mcps ?? 0) },
                  { label: "Calls today", value: statsPending ? "—" : (summary?.calls_today ?? 0) },
                  { label: "Total calls", value: statsPending ? "—" : (summary?.total_calls ?? 0) },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    borderRadius: 10, padding: "8px 6px", textAlign: "center",
                  }} className="bg-muted">
                    <p style={{ fontSize: 16, fontWeight: 700, margin: 0, fontVariantNumeric: "tabular-nums" }}
                      className="text-foreground">{value}</p>
                    <p style={{ fontSize: 10, margin: "2px 0 0", lineHeight: 1.3 }}
                      className="text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>

              {summary && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                    <span style={{ fontSize: 11 }} className="text-muted-foreground">Conversions this month</span>
                    <span style={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums" }} className="text-foreground">
                      {isPro ? `${used} · unlimited` : `${used} / ${limit}`}
                    </span>
                  </div>
                  {!isPro && (
                    <>
                      <div style={{ height: 5, borderRadius: 99, overflow: "hidden" }} className="bg-muted">
                        <div style={{
                          height: "100%", borderRadius: 99,
                          width: `${usagePct}%`,
                          background: atLimit ? "#ef4444" : "linear-gradient(90deg, #6366f1, #8b5cf6)",
                          transition: "width 0.4s ease",
                        }} />
                      </div>
                      {atLimit && (
                        <p style={{ fontSize: 11, marginTop: 5, fontWeight: 500 }} className="text-amber-500">
                          Limit reached —{" "}
                          <Link to="/pricing" style={{ textDecoration: "underline" }} onClick={() => setOpen(false)}>
                            upgrade to Pro
                          </Link>
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Soft upgrade nudge ── */}
            {showNudge && (
              <div style={{
                margin: "0 10px 8px",
                padding: "10px 12px",
                borderRadius: 10,
                background: "#fffbeb",
                border: "1px solid #fcd34d",
              }}>
                <p style={{ margin: 0, fontSize: 12, color: "#92400e", fontWeight: 500 }}>
                  {conversionsLeft === 1 ? "⚡ Last conversion remaining this month!" : `⚡ Only ${conversionsLeft} conversions left this month`}
                </p>
                <Link
                  to="/pricing"
                  onClick={() => setOpen(false)}
                  style={{
                    display: "inline-block", marginTop: 6, fontSize: 11, fontWeight: 600,
                    color: "#d97706", textDecoration: "underline",
                  }}
                >
                  Upgrade to Pro for unlimited →
                </Link>
              </div>
            )}

            {/* ── Menu items ── */}
            <div style={{ padding: "6px" }}>
              {[
                { to: "/dashboard", icon: <LayoutDashboard size={14} />, label: "Dashboard" },
                {
                  to: "/pricing",
                  icon: isPro ? <Crown size={14} className="text-violet-500" /> : <Zap size={14} className="text-amber-500" />,
                  label: isPro ? "Manage subscription" : "Upgrade to Pro",
                },
                ...(isPro
                  ? [{ to: "/dashboard?tab=analytics", icon: <BarChart3 size={14} className="text-blue-500" />, label: "Usage analytics" }]
                  : []),
              ].map(({ to, icon, label }) => (
                <Link
                  key={label}
                  to={to}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 10px", borderRadius: 10,
                    fontSize: 13, textDecoration: "none",
                    transition: "background 0.12s",
                  }}
                  className="text-foreground hover:bg-accent"
                >
                  <span className="text-muted-foreground">{icon}</span>
                  {label}
                </Link>
              ))}

              <div style={{ height: 1, margin: "4px 6px" }} className="bg-border/50" />

              <button
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onSignOut(); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  width: "100%", padding: "9px 10px", borderRadius: 10,
                  fontSize: 13, border: "none", background: "none", cursor: "pointer",
                  transition: "background 0.12s",
                  textAlign: "left",
                }}
                className="text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserAccountMenu;
